import React, { useCallback, useEffect, useState } from "react";
import FixedAgent from "../../utils/api/FixedAgent";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Table from "../../components/table/Table";
import { useSelector } from "react-redux";
import styles from "./History.module.css";
import TableLoader from "../../components/ui/TableLoader";
import PolicyCorrectionModal from "../../components/history/PolicyCorrectionModal";
import Pagination from "../../components/list/Pagination";
import usePaginatedList from "../../hooks/usePaginatedList";
import { toPaginatedResult } from "../../utils/pagination";

function AllHistory() {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const base_url = process.env.REACT_APP__BASE_URL;

  const token = useSelector((state) => state.userData.loginData.token);
  const accountType = useSelector(
    (state) => state.userData.loginData.account_type,
  );
  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const handleEditClick = (order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };
  

  const fetchPage = useCallback(
    async ({ page, perPage }) => {
      const response = await FixedAgent.getManualOrderHistory({
        token,
        page,
        limit: perPage,
        search: debouncedSearch,
      });
      return toPaginatedResult(response.data?.data);
    },
    [token, debouncedSearch],
  );

  const {
    items: allTransactions,
    pagination,
    isLoading: pageIsLoading,
    error,
    setPage: setCurrentPage,
  } = usePaginatedList({ fetchPage, resetKey: debouncedSearch });

  useEffect(() => {
    const handler = setTimeout(() => {
      if (debouncedSearch !== searchInput) {
        setDebouncedSearch(searchInput);
        setCurrentPage(1); // Reset to page 1 on new search
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput, debouncedSearch, setCurrentPage]);

  useEffect(() => {
    if (!error) return;
    toast.error(error?.response?.data?.message || "Failed to load order history", {
      position: "top-left",
      theme: "dark",
    });
    console.error("Failed to load order history", error);
  }, [error]);

  const downloadPolicyHandler = (plate_no) => {
    const url = `${base_url}/app/fixedagent/customer/order/ieiplc/download/${plate_no}`;
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    const linkName = `${plate_no}.pdf`;
    link.download = linkName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      <ToastContainer />

      {!pageIsLoading ? (
        <div>
          <div className={styles.filter_wrapper}>
            {/* Polished search input */}
            <div className={styles.search_container}>
              <span className={styles.search_icon}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="11" cy="11" r="7" stroke="#9CA3AF" strokeWidth="2"/>
                  <path d="M16.5 16.5L21 21" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </span>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by plate number"
                className={styles.filter_input}
              />
            </div>
            {/* Export button with icon */}
            <button className={styles.export_button}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 3v13M7 11l5 5 5-5" stroke="#1814F3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M4 20h16" stroke="#1814F3" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Export
            </button>
          </div>{" "}
          <Table
            tableData={allTransactions}
            table_type="order"
            downloadPolicyHandler={downloadPolicyHandler}
            onEditClick={accountType !== "DISTRIBUTOR" ? handleEditClick : null}
          />
          
          <Pagination
            pagination={pagination}
            onPageChange={setCurrentPage}
            isLoading={pageIsLoading}
            itemLabel="orders"
          />
        </div>
      ) : (
        <TableLoader />
      )}
      <PolicyCorrectionModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        orderData={selectedOrder}
      />
    </div>
  );
}

export default AllHistory;
